// The mark on a message you sent, shared by both apps so "read" means one thing.
//
// A message carries who it was for (`audience`, frozen at publish) and how
// many of them have read it (`readCount`, derived from the receipt rows).
// Everything the UI shows is derived from those two and, in the info view,
// the rows themselves.

import type { CommsMessage, MessageReceipt } from '../../interfaces/ICommsApi.js';

/** What the mark on a message says. */
export type ReadMark =
  | 'pending' // not yet accepted by the server
  | 'failed'
  | 'sent' // nobody it was for has read it yet — a red mark
  | 'partial' // some have — a grey mark
  | 'read'; // everyone it was for has — a green mark

/**
 * The mark on one of your own messages.
 *
 * `audience` excludes nobody, so the people it was *for* are its members
 * minus the author. A message with no one else in the conversation can never
 * be "read": there is no one to read it. A local row the server has not
 * confirmed yet has no audience and stays at `sent`.
 */
export function readMark(message: CommsMessage): ReadMark {
  if (message.status === 'pending' || message.status === 'failed') return message.status;
  const others = (message.audience?.length ?? 0) - 1;
  const read = message.readCount ?? 0;
  if (others <= 0 || read === 0) return 'sent';
  return read >= others ? 'read' : 'partial';
}

/** "read by 3 of 8" — null when there is nothing meaningful to count. */
export function readProgress(message: CommsMessage): { read: number; of: number } | null {
  const of = (message.audience?.length ?? 0) - 1;
  if (of <= 0) return null;
  return { read: Math.min(message.readCount ?? 0, of), of };
}

export interface IReadersOfMessage {
  read: MessageReceipt[]; // Who has read it, most recent reader first, with their time.
  noEvidence: string[]; // Who it was for and has not — in the audience's own order.
}

/**
 * Split a message's audience by what the record holds. The author is never
 * part of their own audience, and a receipt from outside it (someone who has
 * since left the channel) is not shown against a population it is not in.
 */
export function readersOfMessage(
  audience: string[],
  receipts: MessageReceipt[],
  authorId: string,
): IReadersOfMessage {
  const population = new Set(audience.filter((id) => id !== authorId));
  const read = receipts
    .filter((r) => population.has(r.userId))
    .sort((a, b) => new Date(b.readAt).getTime() - new Date(a.readAt).getTime());
  const hasRead = new Set(read.map((r) => r.userId));
  return { read, noEvidence: [...population].filter((id) => !hasRead.has(id)) };
}
